'use client'

import React, { useEffect, useRef, useState } from 'react';
import { getCategoryColor } from '@/components/features/ActivityGraph/utils/styleUtils';

interface ShareOfVoiceReachProps {
  data: Array<{ name: string; reach: number }>;
  className?: string;
  brandOrder?: Array<{ name: string; count: number }>; // Brand order from articles chart for consistent colors
}

// Declare ApexCharts type for TypeScript
declare global {
  interface Window {
    ApexCharts: any;
  }
}

// Helper function to format reach numbers
const formatReach = (reach: number): string => {
  if (reach >= 1000000) {
    return `${(reach / 1000000).toFixed(1)}M`;
  } else if (reach >= 1000) {
    return `${(reach / 1000).toFixed(1)}K`;
  }
  return reach.toString();
};

/**
 * Calculates luminance of a hex color for contrast determination
 */
const getLuminance = (hex: string): number => {
  let r = 0, g = 0, b = 0;
  // 3 digits
  if (hex.length === 4) {
    r = parseInt(hex[1] + hex[1], 16);
    g = parseInt(hex[2] + hex[2], 16);
    b = parseInt(hex[3] + hex[3], 16);
  // 6 digits
  } else if (hex.length === 7) {
    r = parseInt(hex.substring(1, 3), 16);
    g = parseInt(hex.substring(3, 5), 16);
    b = parseInt(hex.substring(5, 7), 16);
  }
  // Normalize RGB values to 0-1 range
  const rN = r / 255;
  const gN = g / 255;
  const bN = b / 255;

  // Apply gamma correction
  const rG = rN <= 0.03928 ? rN / 12.92 : Math.pow((rN + 0.055) / 1.055, 2.4);
  const gG = gN <= 0.03928 ? gN / 12.92 : Math.pow((gN + 0.055) / 1.055, 2.4);
  const bG = bN <= 0.03928 ? bN / 12.92 : Math.pow((bN + 0.055) / 1.055, 2.4);
  
  // Calculate luminance
  return 0.2126 * rG + 0.7152 * gG + 0.0722 * bG;
};

/**
 * Determines the best contrasting text color (black or white) for a given background color.
 */
const getContrastingTextColor = (backgroundColorHex: string): string => {
  const luminance = getLuminance(backgroundColorHex);
  // Use white text for dark backgrounds (luminance < 0.5) and black text for light backgrounds
  return luminance < 0.5 ? '#ffffff' : '#1f2937'; // white or gray-800
};

/**
 * Get consistent color for a brand across all charts
 * This replicates the exact logic used by ActivityGraph's processData
 */
const getBrandColor = (brandName: string, sortedBrandsData: Array<{name: string, count?: number, reach?: number}>): string => {
  // Find the index of this brand in the sorted array, which matches how ActivityGraph processes data
  const index = sortedBrandsData.findIndex(brand => brand.name === brandName);
  return getCategoryColor(brandName, index >= 0 ? index : 0);
};

export const ShareOfVoiceReach: React.FC<ShareOfVoiceReachProps> = ({ data, className = "", brandOrder }) => {
  const chartRef = useRef<HTMLDivElement>(null);
  const chartInstanceRef = useRef<any>(null);
  const [isApexChartsLoaded, setIsApexChartsLoaded] = useState(false);
  const isCleaningUpRef = useRef(false);

  useEffect(() => {
    // Load ApexCharts dynamically
    const loadApexCharts = async () => {
      try {
        if (!window.ApexCharts) {
          console.log('ShareOfVoiceReach: Loading ApexCharts...');
          const ApexCharts = (await import('apexcharts')).default;
          window.ApexCharts = ApexCharts;
          console.log('ShareOfVoiceReach: ApexCharts loaded successfully');
        }
        setIsApexChartsLoaded(true);
      } catch (error) {
        console.error('ShareOfVoiceReach: Error loading ApexCharts:', error);
      }
    };

    loadApexCharts();
  }, []);

  useEffect(() => {
    const chartElement = chartRef.current;

    // Cleanup function: This is the safest place to destroy.
    const cleanup = () => {
      if (chartInstanceRef.current && chartElement && !isCleaningUpRef.current) {
        isCleaningUpRef.current = true;
        console.log('ShareOfVoiceReach: Effect cleanup - Destroying chart');
        
        try {
          // Check if the element is still in the DOM
          if (document.body.contains(chartElement)) {
            chartInstanceRef.current.destroy();
            console.log('ShareOfVoiceReach: Chart destroyed successfully in effect cleanup');
          } else {
            console.log('ShareOfVoiceReach: Skipping chart destroy - element removed from DOM');
          }
        } catch (error) {
          console.warn('ShareOfVoiceReach: Error destroying chart in effect cleanup:', error);
        }
        
        chartInstanceRef.current = null;
        isCleaningUpRef.current = false;
      }
    };

    if (!chartElement || !window.ApexCharts || !isApexChartsLoaded) {
      return cleanup; // Still return cleanup in case instance exists
    }

    // If no data, ensure chart is destroyed and then do nothing else.
    if (!data || data.length === 0) {
      cleanup(); // Destroy if exists
      return; // Explicitly return after cleanup
    }

    // Calculate total reach for percentage calculation
    const totalReach = data.reduce((sum, item) => sum + item.reach, 0);
    
    // Sort data by reach (descending) and take top 6 items for main display
    const sortedData = [...data].sort((a, b) => b.reach - a.reach);
    const topItems = sortedData.slice(0, 6);
    
    // Calculate "Other" category if needed
    const topItemsReach = topItems.reduce((sum, item) => sum + item.reach, 0);
    const otherReach = totalReach - topItemsReach;
    
    // Prepare final data including "Other" if there are remaining items
    let finalData = [...topItems];
    if (otherReach > 0 && data.length > 6) {
      finalData.push({ name: 'Other', reach: otherReach });
    }

    // Generate colors using the same logic as ActivityGraph
    const colors = finalData.map((item) => {
      if (item.name === 'Other') {
        return '#9CA3AF'; // gray-400 for Other category
      }
      // Use brandOrder if provided for consistent colors, otherwise fall back to sortedData
      const referenceOrder = brandOrder || sortedData.map(d => ({ name: d.name, count: d.reach }));
      return getBrandColor(item.name, referenceOrder);
    });

    // Prepare data for treemap
    const chartData = finalData.map((item) => ({
      x: item.name,
      y: item.reach
    }));

    const options = {
      series: [
        {
          data: chartData
        }
      ],
      legend: {
        show: false
      },
      chart: {
        height: 300,
        type: 'treemap',
        toolbar: {
          show: false
        },
        animations: {
          enabled: true,
          easing: 'easeinout',
          speed: 800
        }
      },
      title: {
        text: '',
        align: 'center'
      },
      colors: colors,
      plotOptions: {
        treemap: {
          distributed: true,
          enableShades: false,
          dataLabels: {
            format: 'truncate'
          },
          colorScale: {
            ranges: []
          }
        }
      },
      dataLabels: {
        enabled: true,
        style: {
          fontSize: '12px',
          fontWeight: 'bold',
          fontFamily: 'inherit',
          textOutline: 'none'
        },
        formatter: function(text: string, op: any) {
          const percentage = totalReach > 0 ? Math.round((op.value / totalReach) * 100) : 0;
          const color = colors[op.seriesIndex] || colors[0];
          const textColor = getContrastingTextColor(color);
          
          op.w.config.dataLabels.style.colors = [textColor];
          
          const brandName = text;
          
          if (percentage < 5) {
            return [brandName];
          }
          
          return [brandName, `${percentage}%`, `${formatReach(op.value)}`];
        },
        offsetY: -5,
        offsetX: 0,
        textAnchor: 'middle',
        distributed: false,
        dropShadow: {
          enabled: false
        },
        background: {
          enabled: false
        }
      },
      tooltip: {
        enabled: true,
        y: {
          formatter: function(value: number) {
            const percentage = totalReach > 0 ? Math.round((value / totalReach) * 100) : 0;
            return `${formatReach(value)} reach (${percentage}%)`;
          }
        },
        style: {
          fontSize: '12px'
        }
      },
      states: {
        hover: {
          filter: {
            type: 'lighten',
            value: 0.15
          }
        }
      }
    };

    try {
      // If chart instance exists, try to update. Otherwise, create new.
      if (chartInstanceRef.current && !isCleaningUpRef.current) {
        console.log('ShareOfVoiceReach: Updating existing chart');
        // Before updating, ensure the DOM element is still valid
        if (chartElement && document.body.contains(chartElement)) {
            chartInstanceRef.current.updateOptions(options, true, true); // true to redraw, true to animate
        } else {
            console.warn('ShareOfVoiceReach: Chart element removed from DOM before update, recreating chart.');
            cleanup(); // Clean up old instance
            if (isApexChartsLoaded) {
              chartInstanceRef.current = new window.ApexCharts(chartElement, options);
              chartInstanceRef.current.render();
            }
        }
      } else if (!isCleaningUpRef.current && isApexChartsLoaded) {
        console.log('ShareOfVoiceReach: Creating new chart');
        chartInstanceRef.current = new window.ApexCharts(chartElement, options);
        chartInstanceRef.current.render();
      }
      console.log('ShareOfVoiceReach: Chart operation successful');
    } catch (error) {
      console.error('ShareOfVoiceReach: Error operating on chart:', error);
    }

    return cleanup; // Return the cleanup function
  }, [data, isApexChartsLoaded, brandOrder]); // Dependencies

  // Show debugging info if no data
  if (!data || data.length === 0) {
    return (
      <div className={`flex h-full items-center justify-center p-4 ${className}`}>
        <div className="text-center">
          <div className="text-gray-500 mb-2">No reach data available</div>
          <div className="text-xs text-gray-400">
            Data: {data ? `${data.length} items` : 'null'}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`w-full h-full ${className}`}>
      <div ref={chartRef} className="w-full h-full" />
    </div>
  );
}; 